import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, CalendarX2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function DateOverridesTab() {
  const { currentOrgId } = useAuth();
  const [rows, setRows] = useState([]);
  const [past, setPast] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ start_date: today(), end_date: today(),
    is_unavailable: true, ranges: [{ start: "09:00", end: "12:00" }], reason: "" });
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => { if (currentOrgId) load(); }, [currentOrgId, past]);

  const load = async () => {
    try { const r = await apiFetch(`/scheduling/date-overrides?organization_id=${currentOrgId}&include_past=${past}`);
      setRows(r.overrides || []); } catch (e) { toast.error(e.message); }
  };

  const save = async () => {
    try {
      const r = await apiFetch("/scheduling/date-overrides", {
        method: "POST",
        body: JSON.stringify({ organization_id: currentOrgId, ...form,
          ranges: form.is_unavailable ? [] : form.ranges }),
      });
      if ((r.conflicting_bookings || []).length) {
        setConflicts(r.conflicting_bookings);
        toast.warning(`${r.conflicting_bookings.length} existing booking(s) fall inside this override — please handle them manually.`);
      } else {
        toast.success("Override saved");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this override? Your normal weekly availability will apply again.")) return;
    try { await apiFetch(`/scheduling/date-overrides/${id}`, { method: "DELETE" }); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#57534E]">Adjust your availability for specific dates without changing your weekly schedule.</p>
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#57534E] flex items-center gap-1.5">
            <Switch checked={past} onCheckedChange={setPast} data-testid="overrides-showpast" /> Show past
          </label>
          <Button data-testid="overrides-new" onClick={() => { setForm({ start_date: today(), end_date: today(),
            is_unavailable: true, ranges: [{ start: "09:00", end: "12:00" }], reason: "" }); setConflicts([]); setOpen(true); }}
            className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
            <Plus size={14} className="mr-1.5" /> Add Override
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#E8E2D9] divide-y divide-[#E8E2D9]">
        {rows.length === 0 && <div className="p-8 text-center text-sm text-[#A8A29E]">No overrides.</div>}
        {rows.map((r) => (
          <div key={r.id} className="p-4 flex items-center justify-between gap-3 flex-wrap"
               data-testid={`override-row-${r.id}`}>
            <div>
              <div className="text-sm font-medium">
                {r.start_date}{r.end_date !== r.start_date ? ` – ${r.end_date}` : ""}
              </div>
              <div className="text-xs text-[#57534E]">
                {r.is_unavailable ? "Unavailable" :
                  (r.ranges || []).map((x) => `${x.start}–${x.end}`).join(", ")}
                {r.reason && <span className="ml-2 italic">· {r.reason}</span>}
              </div>
            </div>
            <button onClick={() => remove(r.id)} data-testid={`override-remove-${r.id}`}
                    className="p-1.5 rounded-lg hover:bg-[#F5F5F4] text-[#B02222]">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Add Override</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start date</Label>
                <Input type="date" value={form.start_date}
                       onChange={(e) => setForm({...form, start_date: e.target.value, end_date: form.end_date < e.target.value ? e.target.value : form.end_date})}
                       className="mt-1.5" data-testid="override-start" /></div>
              <div><Label>End date</Label>
                <Input type="date" value={form.end_date} min={form.start_date}
                       onChange={(e) => setForm({...form, end_date: e.target.value})}
                       className="mt-1.5" data-testid="override-end" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.is_unavailable} onCheckedChange={(v) => setForm({...form, is_unavailable: v})}
                      data-testid="override-unavail" />
              Unavailable / Time off
            </label>
            {!form.is_unavailable && (
              <div className="space-y-2">
                <Label>Custom hours</Label>
                {form.ranges.map((rg, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input type="time" value={rg.start}
                           onChange={(e) => setForm({...form, ranges: form.ranges.map((r,i) => i===idx?{...r,start:e.target.value}:r)})}
                           className="w-28" data-testid={`override-start-time-${idx}`} />
                    <span className="text-xs">to</span>
                    <Input type="time" value={rg.end}
                           onChange={(e) => setForm({...form, ranges: form.ranges.map((r,i) => i===idx?{...r,end:e.target.value}:r)})}
                           className="w-28" data-testid={`override-end-time-${idx}`} />
                    <button onClick={() => setForm({...form, ranges: form.ranges.filter((_,i) => i!==idx)})}
                            className="p-1.5 rounded-lg text-[#B02222] hover:bg-[#F5F5F4]">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                <button onClick={() => setForm({...form, ranges: [...form.ranges, { start: "13:00", end: "17:00" }]})}
                        className="text-xs px-2 py-1 rounded-lg border border-[#E8E2D9]">
                  + Add another range
                </button>
              </div>
            )}
            <div><Label>Reason (private)</Label>
              <Input value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})}
                     placeholder="Vacation, Personal, Holiday…" className="mt-1.5" data-testid="override-reason" /></div>
            {conflicts.length > 0 && (
              <div className="p-3 bg-[#FDDCDC] border border-[#F5B0B0] rounded-xl text-xs text-[#B02222]">
                {conflicts.length} existing booking(s) fall inside this override. Please cancel or reschedule them manually.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} data-testid="override-save" className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
